import { useEffect, useState, useCallback, useMemo } from "react";
import api, { fmtErr } from "@/shared/lib/api";
import FileUpload from "@/shared/components/FileUpload";
import {
  IndianRupee, ArrowLeft, ChevronRight, FileText, Download, Layers,
  TrendingUp, CheckCircle2, Banknote, Receipt, Wallet, AlertTriangle, Plus,
} from "lucide-react";
import { toast } from "sonner";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const fileSrc = (u) => (!u ? "" : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

export default function AccountsBillingPortal() {
  const [data, setData] = useState(null);
  const [code, setCode] = useState(null);

  const load = useCallback(() => {
    api.get("/accounts/billing")
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(fmtErr(e.response?.data?.detail) || "Failed to load"); setData({ totals: {}, projects: [] }); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const active = code ? (data?.projects || []).find((p) => p.project_code === code) : null;

  return (
    <div className="min-h-full bg-zinc-50" data-testid="accounts-billing-page">
      <div className="p-5 sm:p-8 max-w-6xl mx-auto">
        {data === null ? (
          <div className="py-24 text-center text-zinc-400">Loading…</div>
        ) : active ? (
          <ProjectView project={active} onBack={() => setCode(null)} onChanged={load} />
        ) : (
          <Overview data={data} onOpen={setCode} />
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, tone }) {
  const tones = {
    emerald: "from-emerald-600 to-emerald-500",
    indigo: "from-indigo-600 to-indigo-500",
    amber: "from-amber-500 to-orange-500",
    rose: "from-rose-600 to-rose-500",
    zinc: "from-zinc-800 to-zinc-700",
  };
  return (
    <div className={`rounded-2xl bg-gradient-to-br ${tones[tone]} text-white p-5 shadow-sm`} data-testid={`bill-kpi-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <div className="flex items-center gap-2 text-white/80 text-xs font-medium uppercase tracking-wider">
        <Icon size={15} strokeWidth={1.8} /> {label}
      </div>
      <div className="mt-2 text-2xl font-bold tracking-tight tabular-nums">{value}</div>
    </div>
  );
}

function Overview({ data, onOpen }) {
  const t = data.totals || {};
  const projects = data.projects || [];
  return (
    <div data-testid="accounts-billing-overview">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-emerald-600 text-white flex items-center justify-center shadow">
          <Banknote size={22} strokeWidth={1.8} />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900">Billing</h1>
          <p className="text-sm text-zinc-500">Pick the line items, raise advance or progress invoices, and track every rupee billed vs received.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <Kpi label="Order value" value={inr(t.revised_value)} icon={TrendingUp} tone="indigo" />
        <Kpi label="Invoiced" value={inr(t.invoiced_value)} icon={Receipt} tone="emerald" />
        <Kpi label="Received" value={inr(t.received_value)} icon={Wallet} tone="zinc" />
        <Kpi label="Outstanding" value={inr(t.outstanding_value)} icon={AlertTriangle} tone="amber" />
        <Kpi label="Yet to bill" value={inr(t.yet_to_bill_value)} icon={IndianRupee} tone="rose" />
      </div>

      <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-3">
        {t.projects_ready || 0} of {t.projects || 0} project(s) have lines ready to bill
      </div>
      <div className="space-y-3">
        {projects.map((p) => (
          <button key={p.project_code} onClick={() => onOpen(p.project_code)}
            data-testid={`accounts-billing-project-${p.project_code}`}
            className="w-full text-left rounded-2xl border border-zinc-200 bg-white p-5 hover:border-emerald-300 hover:shadow-md transition-all flex items-center gap-4">
            <div className={`w-1.5 self-stretch rounded-full ${p.ready_to_bill_count > 0 ? "bg-emerald-400" : "bg-zinc-200"}`} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs text-zinc-400">
                  {p.upr_no ? <span className="text-emerald-700 font-semibold">UPR {p.upr_no}</span> : p.project_code}
                </span>
                <span className="font-semibold text-zinc-900 truncate">{p.name}</span>
                {p.ready_to_bill_count > 0 && (
                  <span className="text-[10px] font-semibold uppercase tracking-wide bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full">{p.ready_to_bill_count} ready to bill</span>
                )}
              </div>
              <div className="text-xs text-zinc-500 mt-0.5">{p.client_name}</div>
              <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs">
                <span className="text-zinc-500">Order <b className="text-zinc-800 tabular-nums">{inr(p.revised_value)}</b></span>
                <span className="text-zinc-500">Invoiced <b className="text-emerald-700 tabular-nums">{inr(p.invoiced_value)}</b></span>
                <span className="text-zinc-500">Received <b className="text-zinc-800 tabular-nums">{inr(p.received_value)}</b></span>
                <span className="text-zinc-500">Yet to bill <b className="text-rose-600 tabular-nums">{inr(p.yet_to_bill_value)}</b></span>
              </div>
            </div>
            <ChevronRight size={18} className="text-zinc-300 shrink-0" />
          </button>
        ))}
        {projects.length === 0 && <div className="py-16 text-center text-zinc-400">No active projects to bill.</div>}
      </div>
    </div>
  );
}

function ProjectView({ project, onBack, onChanged }) {
  const [sel, setSel] = useState(() => new Set());
  const [gst, setGst] = useState(18);
  const [milestone, setMilestone] = useState("progress");
  const [valueOverride, setValueOverride] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [fileName, setFileName] = useState("");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  const lines = project.lines || [];
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allOn = sel.size === lines.length && lines.length > 0;
  const toggleAll = () => setSel(allOn ? new Set() : new Set(lines.map((l) => l.boq_id)));

  const selectedSum = useMemo(
    () => lines.filter((l) => sel.has(l.boq_id)).reduce((a, l) => a + (l.amount || 0), 0),
    [lines, sel]);
  const isAdvance = milestone === "advance";
  const baseValue = isAdvance
    ? (parseFloat(valueOverride) || 0)
    : (valueOverride !== "" ? (parseFloat(valueOverride) || 0) : selectedSum);
  const tax = Math.round(baseValue * (parseFloat(gst) || 0)) / 100;
  const total = Math.round((baseValue + tax) * 100) / 100;

  async function raise() {
    if (!isAdvance && sel.size === 0) { toast.error("Select at least one line to bill."); return; }
    if (baseValue <= 0) { toast.error("Enter an invoice value."); return; }
    setBusy(true);
    try {
      await api.post("/invoices", {
        project_code: project.project_code,
        milestone,
        invoice_value: baseValue,
        tax_amount: tax,
        boq_line_ids: isAdvance ? [] : [...sel],
        file_url: fileUrl, file_name: fileName,
        remarks,
      });
      toast.success(`${isAdvance ? "Advance" : "Invoice"} raised — ${inr(total)} (incl. GST).`);
      setSel(new Set()); setValueOverride(""); setFileUrl(""); setFileName(""); setRemarks("");
      onChanged();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  async function downloadFinancials() {
    try {
      const r = await api.get(`/projects/${project.project_code}/financials/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${project.project_code}-FINANCIALS.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Download failed"); }
  }

  return (
    <div data-testid="accounts-billing-detail">
      <button onClick={onBack} data-testid="accounts-billing-back" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-800 mb-4">
        <ArrowLeft size={16} /> All projects
      </button>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono text-xs text-zinc-400">
            {project.upr_no && <span className="text-emerald-600 font-semibold mr-2" data-testid="billing-upr">UPR {project.upr_no}</span>}
            {project.project_code} · {project.client_name}
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900 mb-5">{project.name}</h1>
        </div>
        <button onClick={downloadFinancials} data-testid="bill-download-financials"
          className="inline-flex items-center gap-2 rounded-lg border border-zinc-300 bg-white px-3.5 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50">
          <Download size={15} /> Download invoices, POs & emails
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-7">
        <Kpi label="Order value" value={inr(project.revised_value)} icon={TrendingUp} tone="indigo" />
        <Kpi label="Invoiced" value={inr(project.invoiced_value)} icon={Receipt} tone="emerald" />
        <Kpi label="Received" value={inr(project.received_value)} icon={Wallet} tone="zinc" />
        <Kpi label="Outstanding" value={inr(project.outstanding_value)} icon={AlertTriangle} tone="amber" />
        <Kpi label="Yet to bill" value={inr(project.yet_to_bill_value)} icon={IndianRupee} tone="rose" />
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-5">
        {/* line picker */}
        <div className="rounded-2xl border border-zinc-200 bg-white overflow-hidden">
          <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between">
            <span className="flex items-center gap-2 font-semibold text-zinc-900"><Layers size={16} className="text-emerald-600" /> Bill of Quantities</span>
            <button onClick={toggleAll} data-testid="bill-select-all" className="text-xs text-emerald-700 hover:text-emerald-900 font-medium">
              {allOn ? "Clear all" : "Select all"}
            </button>
          </div>
          <div className="divide-y divide-zinc-100">
            {lines.map((l) => (
              <label key={l.boq_id} data-testid={`bill-line-${l.boq_id}`}
                className={`flex items-center gap-3 px-5 py-3 cursor-pointer hover:bg-zinc-50 ${sel.has(l.boq_id) ? "bg-emerald-50/50" : ""}`}>
                <input type="checkbox" checked={sel.has(l.boq_id)} onChange={() => toggle(l.boq_id)}
                  data-testid={`bill-line-check-${l.boq_id}`}
                  className="w-4 h-4 rounded border-zinc-300 text-emerald-600 focus:ring-emerald-400" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-zinc-800 truncate">{l.label}</span>
                    {l.accounts_only && <span className="text-[10px] uppercase tracking-wide bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded">Accounts</span>}
                    {l.is_variation && <span className="text-[10px] uppercase tracking-wide bg-violet-50 text-violet-700 border border-violet-200 px-1.5 py-0.5 rounded">Variation</span>}
                    {l.billing_due && <span className="text-[10px] uppercase tracking-wide bg-emerald-600 text-white px-1.5 py-0.5 rounded">Ready to bill</span>}
                    {l.billed && <span className="text-[10px] uppercase tracking-wide bg-zinc-100 text-zinc-500 border border-zinc-200 px-1.5 py-0.5 rounded">Billed</span>}
                  </div>
                  <div className="text-xs text-zinc-400 mt-0.5">
                    {l.quantity ? `${l.quantity} ${l.unit} · ` : ""}
                    {l.production_approved ? "Production approved" : "In progress"}
                  </div>
                </div>
                <div className="text-sm font-semibold text-zinc-900 tabular-nums shrink-0">{inr(l.amount)}</div>
              </label>
            ))}
            {lines.length === 0 && <div className="py-10 text-center text-sm text-zinc-400">No billable lines.</div>}
          </div>
        </div>

        {/* invoice composer */}
        <div className="space-y-4">
          <div className="rounded-2xl border border-emerald-200 bg-white p-5 shadow-sm" data-testid="bill-composer">
            <div className="font-semibold text-zinc-900 mb-3 flex items-center gap-2"><Plus size={16} className="text-emerald-600" /> Raise an invoice</div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-zinc-400">Type</label>
            <select value={milestone} onChange={(e) => setMilestone(e.target.value)} data-testid="bill-milestone"
              className="w-full mt-1 mb-3 text-sm rounded-lg border border-zinc-300 px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-300">
              <option value="advance">Advance</option>
              <option value="progress">Progress</option>
              <option value="final">Final</option>
            </select>

            {isAdvance ? (
              <>
                <div className="mb-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">Advance invoices don't need line selection — just enter the amount.</div>
                <label className="text-[11px] font-mono uppercase tracking-wider text-zinc-400">Advance amount (₹, pre-GST)</label>
                <input type="number" value={valueOverride} onChange={(e) => setValueOverride(e.target.value)}
                  data-testid="bill-advance-amount" placeholder="e.g. 200000"
                  className="w-full mt-1 mb-3 text-sm rounded-lg border border-zinc-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-300" />
              </>
            ) : (
              <div className="mb-3 text-xs text-zinc-500">
                {sel.size} line(s) selected · <b className="text-zinc-800">{inr(selectedSum)}</b>
                <input type="number" value={valueOverride} onChange={(e) => setValueOverride(e.target.value)}
                  data-testid="bill-value-override" placeholder="Override value (optional)"
                  className="w-full mt-2 text-sm rounded-lg border border-zinc-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-300" />
              </div>
            )}

            <label className="text-[11px] font-mono uppercase tracking-wider text-zinc-400">GST %</label>
            <input type="number" value={gst} onChange={(e) => setGst(e.target.value)} data-testid="bill-gst"
              className="w-full mt-1 mb-3 text-sm rounded-lg border border-zinc-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-300" />

            <div className="rounded-lg bg-zinc-50 border border-zinc-200 p-3 text-sm mb-3 space-y-1">
              <div className="flex justify-between text-zinc-500">Taxable<span className="tabular-nums text-zinc-800">{inr(baseValue)}</span></div>
              <div className="flex justify-between text-zinc-500">GST<span className="tabular-nums text-zinc-800">{inr(tax)}</span></div>
              <div className="flex justify-between font-semibold text-zinc-900 pt-1 border-t border-zinc-200">Total<span className="tabular-nums" data-testid="bill-total">{inr(total)}</span></div>
            </div>

            <div className="mb-3">
              <FileUpload value={fileUrl} onChange={(u) => { setFileUrl(u); setFileName(u ? u.split("/").pop() : ""); }}
                label={fileUrl ? "Invoice copy attached" : "Attach invoice copy (optional)"} testid="bill-invoice-upload" />
            </div>
            <input value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Remarks (optional)"
              data-testid="bill-remarks" className="w-full mb-3 text-sm rounded-lg border border-zinc-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-300" />

            <button onClick={raise} disabled={busy} data-testid="bill-raise"
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 text-white px-4 py-2.5 text-sm font-medium hover:bg-emerald-700 disabled:opacity-50">
              <Receipt size={16} /> {busy ? "Raising…" : `Raise ${isAdvance ? "advance" : "invoice"} · ${inr(total)}`}
            </button>
          </div>

          {/* invoices list */}
          <div className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid="bill-invoices">
            <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-3">Invoices ({(project.invoices || []).length})</div>
            {(project.invoices || []).length === 0 ? (
              <div className="text-sm text-zinc-400 py-2">No invoices raised yet.</div>
            ) : (
              <div className="space-y-2">
                {project.invoices.map((iv) => (
                  <div key={iv.id} data-testid={`bill-invoice-${iv.id}`} className="rounded-lg border border-zinc-200 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-zinc-800">{iv.invoice_number}</span>
                      <span className="text-sm font-semibold tabular-nums">{inr(iv.total)}</span>
                    </div>
                    <div className="text-xs text-zinc-400 mt-0.5 flex items-center gap-2 flex-wrap">
                      <span className="capitalize">{iv.milestone}</span>
                      <span>· {iv.invoice_date}</span>
                      <span>· {iv.lines_count} line(s)</span>
                      <span className={`capitalize ${iv.payment_status === "paid" ? "text-emerald-600" : "text-amber-600"}`}>· {iv.payment_status}</span>
                      {iv.file_url && <a href={fileSrc(iv.file_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-emerald-700 hover:underline"><Download size={12} /> copy</a>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
